import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import AudienceDialog from "@/components/AudienceDialog";
import VenueMarquee from "@/components/VenueMarquee";
import { PHOTOS } from "@/lib/photos";

const EASE = [0.4, 0, 0.2, 1];

const reveal = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
};

const NIGHTS = [
  {
    to: "/book",
    title: "Jazz Meets Jewish Interactive Concert Experience",
    blurb:
      "Solo piano or jazz trio plus Dan's storytelling, with live requests from the room. Familiar niggunim and High Holiday melodies taken somewhere they've never been, with the audience shaping where it goes.",
    photo: PHOTOS.jazzMeetsJewish,
    testId: "community-card-jazz",
  },
  {
    to: "/soundbaths",
    title: "Piano Soundbath Immersive Niggunim",
    blurb:
      "Lights low, no talking, just piano. An immersive hour of niggunim your neshama already knows — a culturally-rooted alternative to crystal bowls and gongs.",
    photo: PHOTOS.soundbathHero,
    testId: "community-card-soundbath",
  },
  {
    to: "/events/galas",
    title: "Live Music for Your Gala or Fundraiser",
    blurb:
      "Live piano for the night you're already running — cocktail hour, dinner, the program, the appeal. Dan reads the schedule, watches the room, and plays what the moment needs, giving it warmth and identity without ever competing with your speakers.",
    photo: PHOTOS.galaRoom,
    testId: "community-card-gala",
  },
];

const SHABBATON = [
  {
    n: "Thursday night",
    title: "The concert",
    body: "Any of the three evenings above, with the farbrengen afterwards.",
  },
  {
    n: "Shabbos",
    title: "Scholar in Residence",
    body: "A chassidus class, or a discussion on Torah & Creativity — the ideas underneath the music, unplugged.",
  },
  {
    n: "Shabbos",
    title: "Seder Niggunim",
    body: "A sit-down session of niggunim: singing together, learning where they came from, and what they're doing to us.",
  },
  {
    n: "Saturday night",
    title: "Musical Melave Malka",
    body: "Escorting the Shabbos queen out with music. The loosest, warmest room of the whole weekend.",
  },
];

const goldCta =
  "inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em] transition-colors hover:bg-[#F2D589]/[0.12]";
const goldStyle = { color: "#F2D589", border: "1px solid rgba(242,213,137,0.5)" };
const plainCta =
  "inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em] border border-white/25 transition-colors hover:bg-white/10";

export default function CommunityEvents() {
  return (
    <MarketingShell active="/community-events">
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
        className="grid md:grid-cols-[1.25fr_0.75fr] gap-12 md:gap-14 items-center"
        data-testid="community-hero"
      >
        <div>
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Events &middot; Community events
        </p>
        <h1
          className="leading-[0.95]"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: "clamp(40px, 5.4vw, 78px)",
            textWrap: "balance",
            maxWidth: "22ch",
          }}
        >
          An unforgettable night of <em style={{ color: "#F2D589" }}>story and song</em> for your community.
        </h1>
        <p className="mt-6 max-w-[46rem] text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.8)" }}>
          Shuls, Chabad houses, federations, schools, JCCs — whatever the room,
          Dan builds the evening around it. Jazz language, Jewish soul, and a
          piano that answers the people in front of it.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/contact" className={goldCta} style={goldStyle} data-testid="community-hero-cta">
            Start a conversation
          </Link>
          <Link to="/performances" className={plainCta} data-testid="community-hero-performances">
            Upcoming performances
          </Link>
        </div>
        <p className="mt-5 text-sm" style={{ color: "rgba(245,239,227,0.6)" }} data-testid="community-pricing">
          Events start at $770.
        </p>
        </div>
        <VenueMarquee />
      </motion.section>

      {/* Unforgettable nights */}
      <section className="mt-24" data-testid="community-nights">
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          The experiences
        </p>
        <h2 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, textWrap: "balance", maxWidth: "30ch" }}>
          Three evenings your community will still be talking about next year.
        </h2>
        <p className="mt-4 max-w-[48rem] text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.76)" }}>
          Each one is a full night built around the piano — not background music
          at someone else&apos;s program. Pick the one that fits your room, and
          Dan shapes the rest around the people in it.
        </p>

        <ul className="mt-10 grid md:grid-cols-3 gap-5">
          {NIGHTS.map((n, i) => (
            <motion.li
              key={n.title}
              {...reveal}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
              className="flex"
            >
              <Link
                to={n.to}
                data-testid={n.testId}
                className="group flex h-full flex-col rounded-2xl border overflow-hidden backdrop-blur-md transition-colors hover:border-[#F2D589]/60"
                style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,30,0.45)" }}
              >
                <div className="w-full overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
                  <img
                    src={n.photo}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex flex-col gap-3 flex-1">
                  <h3 className="text-2xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                    {n.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: "rgba(245,239,227,0.7)" }}>
                    {n.blurb}
                  </p>
                  <span
                    className="mt-auto pt-3 text-[11px] uppercase tracking-[0.22em] transition-colors group-hover:text-[#F2D589]"
                    style={{ color: "#FCA07A" }}
                  >
                    Learn more &rarr;
                  </span>
                </div>
              </Link>
            </motion.li>
          ))}
        </ul>
      </section>

      {/* Shabbaton */}
      <section
        className="mt-24 rounded-2xl border p-6 sm:p-10 backdrop-blur-md"
        style={{ borderColor: "rgba(242,213,137,0.28)", backgroundColor: "rgba(8,4,30,0.5)" }}
        data-testid="community-shabbaton"
      >
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Go bigger
        </p>
        <h2 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Turn any of them into a full Shabbaton.
        </h2>
        <p className="mt-4 max-w-[46rem] text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.78)" }}>
          A whole weekend built around the music, with Dan in residence from
          Thursday night through Saturday night. Quoted per weekend.
        </p>

        <ul className="mt-10 grid sm:grid-cols-2 gap-x-12 gap-y-8">
          {SHABBATON.map((s, i) => (
            <motion.li
              key={s.title}
              {...reveal}
              transition={{ duration: 0.6, ease: EASE, delay: i * 0.07 }}
              className="pl-6"
              style={{ borderLeft: "1px solid rgba(242,213,137,0.3)" }}
              data-testid={`community-shabbaton-${i}`}
            >
              <p className="text-[10px] uppercase tracking-[0.26em]" style={{ color: "#FCA07A" }}>
                {s.n}
              </p>
              <h3 className="mt-2 text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {s.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(245,239,227,0.7)" }}>
                {s.body}
              </p>
            </motion.li>
          ))}
        </ul>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/contact?type=Community%20Event%20Booking&topic=Shabbaton"
            className={goldCta}
            style={goldStyle}
            data-testid="community-shabbaton-cta"
          >
            Ask about a Shabbaton weekend
          </Link>
        </div>
      </section>

      {/* Tour alerts for community leaders */}
      <section
        className="mt-24 rounded-2xl border p-6 sm:p-10 backdrop-blur-md"
        style={{ borderColor: "rgba(252,160,122,0.35)", backgroundColor: "rgba(8,4,30,0.5)" }}
        data-testid="community-tour-alerts"
      >
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#FCA07A" }}>
          For community leaders
        </p>
        <h2
          className="leading-tight"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            // One line at every width — the string is the design here.
            fontSize: "clamp(15px, 3.3vw, 40px)",
            whiteSpace: "nowrap",
          }}
          data-testid="community-tour-alerts-heading"
        >
          Catch Dan when he&apos;s already coming your way.
        </h2>
        <p className="mt-4 max-w-[48rem] text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.78)" }}>
          Travel is usually the biggest line in the budget. Tell us where your
          community is and we&apos;ll email you when Dan is touring nearby —
          when he&apos;s already in the region, the travel cost gets split or
          disappears, and a night that felt out of reach suddenly isn&apos;t.
        </p>
        <ul className="mt-6 space-y-2 text-sm" style={{ color: "rgba(245,239,227,0.68)" }}>
          <li>&middot; A heads-up before dates in your area get filled</li>
          <li>&middot; Reduced or shared travel fees when Dan&apos;s already close</li>
          <li>&middot; No obligation, and no list-swapping — ever</li>
        </ul>
        <div className="mt-8">
          <AudienceDialog
            testIdPrefix="tour-alert-dialog"
            source="community-events-tour-alerts"
            program="Tour Alerts — Community Leaders"
            eyebrow="Tour alerts"
            title="Tell me when Dan's nearby."
            description={
              <>
                We&apos;ll email you when Dan is touring near your community, so
                you can grab a date while the travel cost is already covered by
                the trip.
              </>
            }
            submitLabel="Notify me when Dan's nearby →"
            doneTitle="You're on the list."
            doneDescription={<>Next time Dan is heading your way, you&apos;ll hear from us first.</>}
            trigger={
              <button
                type="button"
                data-testid="community-tour-alert-trigger"
                className="inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em]"
                style={{ backgroundColor: "#F2D589", color: "#080419", fontWeight: 600 }}
              >
                Notify me when Dan&apos;s nearby &rarr;
              </button>
            }
          />
        </div>
      </section>

    </MarketingShell>
  );
}
